import React, { useState } from "react";
import { useDispatch } from "react-redux";
import PsrCameraApi from "../../../api/PsrCameraApi";
import {
  PsrCaptureProfile
} from "../../../Model/CaptureProfile";
import { getModuleCapability } from "../../../redux/reducers/ModuleCapabilitiesReducer";
import { getModule } from "../../../redux/reducers/ModuleReducer";
import { getModuleState } from "../../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import MtiButton from "../../Buttons/MtiButton";
import SwitchButton from "../../Buttons/SwitchButton";
import { PsrBiningControl } from "./PsrCameraControl/PsrBinningControl";
import { PsrBrightnessControl } from "./PsrCameraControl/PsrBrightnessControl";
import { PsrExposureControl } from "./PsrCameraControl/PsrExposureControl";
import { PsrFocusControl } from "./PsrCameraControl/PsrFocusControl";
import { PsrGainControl } from "./PsrCameraControl/PsrGainControl";
import { PsrImageInfoControl } from "./PsrCameraControl/PsrImageInfoControl";

type Props = {
  psrIdentifier: string;
  psrCameraId: number;
  disabled?: boolean;
  additionalControlDisabled?: boolean;
  style?: React.CSSProperties;
};

type AutoExposureControlProps = {
  onToggle: () => void;
  isOn: boolean;
  disabled: boolean;
};

function AutoExposureControl({
  onToggle,
  isOn,
  disabled,
}: AutoExposureControlProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        marginTop: 10,
        paddingLeft: 9,
      }}
    >
      <div
        style={{
          fontSize: "14px",
          fontWeight: 700,
          color: "#000000",
          marginRight: 18,
        }}
      >
        Auto Exposure
      </div>
      <SwitchButton
        isOn={isOn}
        onToggle={onToggle}
        disabled={disabled}
        withLetter={true}
      />
    </div>
  );
}

type AutoFocusControlProps = {
  onClick: () => void;
  disabled: boolean;
};

function AutoFocusControl({ onClick, disabled }: AutoFocusControlProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        marginTop: 10,
        paddingLeft: 9,
      }}
    >
      <MtiButton
        size="medium"
        disabled={disabled}
        style={{
          letterSpacing: 1.25,
          paddingLeft: 16,
          paddingRight: 16,
        }}
        onClick={onClick}
      >
        Auto Focus
      </MtiButton>
    </div>
  );
}

export default function PsrCameraControl({
  psrIdentifier,
  psrCameraId,
  disabled = false,
  additionalControlDisabled = false,
  style = {},
}: Props) {
  const dispatch = useDispatch();
  const psrModule = useTypedSelector((state) =>
    getModule(state, psrIdentifier)
  );
  const moduleState = useTypedSelector((state) =>
    getModuleState(state, psrIdentifier)
  );
  const moduleCapability = useTypedSelector((state) =>
    getModuleCapability(state, psrIdentifier)
  );

  const psrState = moduleState.psr;
  const psrCapability = moduleCapability.psr;

  const [isChangingCaptureProfile, setIsChangingCaptureProfile] =
    useState(false);


  if (psrState === undefined) {
    throw new Error("Psr state is null");
  }

  if (psrCapability === undefined) {
    throw new Error("Psr Capability is null");
  }

  if (psrCapability.psrCameras === undefined) {
    throw new Error("Psr Capability is null");
  }

  const psrCameraCapability = psrCapability?.psrCameras?.find(
    (x) => x.psrCameraId === psrCameraId
  );

  const psrCameraState = psrState.psrCameras.find(
    (x) => x.psrCameraId === psrCameraId
  );

  const psrCaptureProfile = psrCameraState?.captureProfile;

  if (psrCameraState === undefined) {
    throw new Error("Psr Camera state is null");
  }

  if (psrCameraCapability === undefined) {
    throw new Error("Psr Camera Capability is null");
  }

  if (psrCaptureProfile == null) {
    throw new Error("Psr Camera Capture Profile is null");
  }

  const onCaptureProfileChanged = (cp: PsrCaptureProfile) => {
    console.log("DEBUG", "onCaptureProfileChanged", { cp });
    setIsChangingCaptureProfile(true);
    PsrCameraApi.setCaptureProfile(
      psrModule.identifier,
      psrCameraId,
      cp
    ).finally(() => setIsChangingCaptureProfile(false));
  };

  const onAutoFocus = () => {
    PsrCameraApi.doAutoFocus(psrModule.identifier, psrCameraId);
  };

  const onAutoExposure = () => {
    onCaptureProfileChanged({
      ...psrCaptureProfile,
      autoExposure: {
        useAutoExposure: !(
          psrCaptureProfile?.autoExposure?.useAutoExposure ?? false
        ),
      },
    });
  };

  const focus = psrCameraCapability.focus;
  const gain = psrCameraCapability.gain;
  const brightness = psrCameraCapability.brightness;
  const exposure = psrCameraCapability.exposure;
  const binning = psrCameraCapability.binning;

  let control: JSX.Element[] = [];

  if (focus) {
    control.push(
      <PsrFocusControl
        captureProfile={psrCaptureProfile}
        focus={focus}
        disabled={false}
        onChange={onCaptureProfileChanged}
      />
    );
  }

  // TODO: uncomment this when bakcend is supported
  // if (gain) {
  //   control.push(
  //     <PsrGainControl
  //       captureProfile={psrCaptureProfile}
  //       gain={gain}
  //       disabled={false}
  //       onChange={onCaptureProfileChanged}
  //     />
  //   );
  // }

  if (exposure) {
    control.push(
      <AutoExposureControl
        isOn={psrCaptureProfile.autoExposure?.useAutoExposure ?? false}
        onToggle={onAutoExposure}
        disabled={false}
      />
    );
  }

  if (exposure) {
    control.push(
      <PsrExposureControl
        captureProfile={psrCaptureProfile}
        exposure={exposure}
        disabled={psrCaptureProfile.autoExposure?.useAutoExposure ?? false}
        onChange={onCaptureProfileChanged}
      />
    );
  }

  if (brightness) {
    control.push(
      <PsrBrightnessControl
        captureProfile={psrCaptureProfile}
        brightness={brightness}
        disabled={false}
        onChange={onCaptureProfileChanged}
      />
    );
  }

  if (binning) {
    control.push(
      <PsrBiningControl
        captureProfile={psrCaptureProfile}
        binning={binning}
        disabled={false}
        onChange={onCaptureProfileChanged}
      />
    );
  }

  control.push(
    <PsrImageInfoControl
      psrCameraState={psrCameraState}
      captureProfile={psrCaptureProfile}
      disabled={false}
    />
  );

  control.push(<AutoFocusControl disabled={false} onClick={onAutoFocus} />);

  return (
    <div>
      {/*--------CAMERA CONTROL--------*/}
      <div
        style={{
          margin: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              minHeight: 100,
              border: "1px solid #E0E0E0",
              borderRadius: "10px",
              padding: "9px 11px 9px 11px",
            }}
          >
            {control}
          </div>
        </div>
      </div>
    </div>
  );
}
